import { TokenType } from "types/token.type";
import { BPS_DENOMINATOR, NORMALIZED_SUPPLY_TOKENS, TRADE_FEE_BPS } from "utils/constants";

export interface CurveQuoteResult {
  tokensOut: number;
  ethOut: number;
  fee: number;
  netInput: number;
  nextVirtualEth: number;
  nextVirtualToken: number;
}

export const getSupplyScalingFactor = (token: Pick<TokenType, "tokenSupply">): number => {
  const supply = Number(token.tokenSupply);
  if (!Number.isFinite(supply) || supply <= 0) {
    return 1;
  }
  return NORMALIZED_SUPPLY_TOKENS / supply;
};

const sanitizeReserve = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
};

export const getCurveBuyQuote = (
  token: Pick<TokenType, "tokenSupply" | "tokenReserves" | "ethReserves">,
  ethAmount: number
): CurveQuoteResult => {
  const vReserveEth = sanitizeReserve(token.ethReserves);
  const vReserveToken = sanitizeReserve(token.tokenReserves);
  const supply = sanitizeReserve(token.tokenSupply);

  if (ethAmount <= 0 || vReserveEth === 0 || vReserveToken === 0 || supply === 0) {
    return {
      tokensOut: 0,
      ethOut: 0,
      fee: 0,
      netInput: 0,
      nextVirtualEth: vReserveEth,
      nextVirtualToken: vReserveToken,
    };
  }

  const fee = (ethAmount * TRADE_FEE_BPS) / BPS_DENOMINATOR;
  const netEthIn = Math.max(ethAmount - fee, 0);

  if (netEthIn === 0) {
    return {
      tokensOut: 0,
      ethOut: 0,
      fee,
      netInput: netEthIn,
      nextVirtualEth: vReserveEth,
      nextVirtualToken: vReserveToken,
    };
  }

  const newReserveEth = vReserveEth + netEthIn;
  const newReserveToken = (vReserveEth * vReserveToken) / newReserveEth;
  const virtualTokensOut = Math.max(vReserveToken - newReserveToken, 0);
  const scaling = getSupplyScalingFactor({ tokenSupply: supply } as TokenType);
  const actualTokensOut = scaling > 0 ? virtualTokensOut / scaling : 0;

  return {
    tokensOut: actualTokensOut,
    ethOut: 0,
    fee,
    netInput: netEthIn,
    nextVirtualEth: newReserveEth,
    nextVirtualToken: newReserveToken,
  };
};

export const getCurveSellQuote = (
  token: Pick<TokenType, "tokenSupply" | "tokenReserves" | "ethReserves">,
  tokenAmount: number
): CurveQuoteResult => {
  const vReserveEth = sanitizeReserve(token.ethReserves);
  const vReserveToken = sanitizeReserve(token.tokenReserves);
  const supply = sanitizeReserve(token.tokenSupply);

  if (tokenAmount <= 0 || vReserveEth === 0 || vReserveToken === 0 || supply === 0) {
    return {
      tokensOut: 0,
      ethOut: 0,
      fee: 0,
      netInput: 0,
      nextVirtualEth: vReserveEth,
      nextVirtualToken: vReserveToken,
    };
  }

  const virtualTokenAmount = (tokenAmount * NORMALIZED_SUPPLY_TOKENS) / supply;
  if (!Number.isFinite(virtualTokenAmount) || virtualTokenAmount <= 0) {
    return {
      tokensOut: 0,
      ethOut: 0,
      fee: 0,
      netInput: 0,
      nextVirtualEth: vReserveEth,
      nextVirtualToken: vReserveToken,
    };
  }

  const newReserveToken = vReserveToken + virtualTokenAmount;
  const newReserveEth = (vReserveEth * vReserveToken) / newReserveToken;
  const grossEthOut = Math.max(vReserveEth - newReserveEth, 0);
  const fee = (grossEthOut * TRADE_FEE_BPS) / BPS_DENOMINATOR;
  const netEthOut = Math.max(grossEthOut - fee, 0);

  return {
    tokensOut: tokenAmount,
    ethOut: netEthOut,
    fee,
    netInput: tokenAmount,
    nextVirtualEth: newReserveEth,
    nextVirtualToken: newReserveToken,
  };
};

export const scaleImpliedUsdPrice = (
  impliedUsdPrice: number,
  token: Pick<TokenType, "tokenSupply">
) => {
  const scaling = getSupplyScalingFactor(token);
  return impliedUsdPrice * scaling;
};

